// Add or edit one registry source, and read its permission history.
//
// The permission history is the useful half of this page. "Why is this
// source off?" is answered by the verdict trail, not by the config
// form — and the trail is kept precisely so "when did this outlet
// start blocking us" has an answer.

import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Save, Trash2 } from 'lucide-react';
import { sourcesApi, ApiError } from '../lib/api';
import type {
  Source,
  SourcePermissionCheck,
  SourceCategory,
  RetrievalMethod,
  CrawlSourceMode,
  SourceCadence,
} from '@shared/types';

const CATEGORIES: SourceCategory[] = ['outlet', 'municipal', 'institution', 'service'];
const METHODS: RetrievalMethod[] = ['crawl', 'rss', 'sitemap', 'search_only', 'manual'];
const CRAWL_SOURCES: CrawlSourceMode[] = ['all', 'sitemaps', 'links'];
const CADENCES: SourceCadence[] = ['hourly', 'daily', 'weekly'];

interface FormState {
  slug: string;
  name: string;
  homepage_url: string;
  category: SourceCategory;
  town: string;
  independence_group: string;
  retrieval_method: RetrievalMethod;
  start_url: string;
  feed_url: string;
  crawl_source: CrawlSourceMode;
  crawl_render: boolean;
  crawl_limit: number;
  crawl_depth: number;
  crawl_max_age: number;
  cadence: SourceCadence;
  include_patterns: string;
  exclude_patterns: string;
  enabled: boolean;
  notes: string;
}

const EMPTY: FormState = {
  slug: '',
  name: '',
  homepage_url: '',
  category: 'outlet',
  town: '',
  independence_group: '',
  retrieval_method: 'crawl',
  start_url: '',
  feed_url: '',
  crawl_source: 'all',
  crawl_render: false,
  crawl_limit: 25,
  crawl_depth: 2,
  crawl_max_age: 86400,
  cadence: 'daily',
  include_patterns: '',
  exclude_patterns: '',
  enabled: true,
  notes: '',
};

function toForm(s: Source): FormState {
  return {
    slug: s.slug,
    name: s.name,
    homepage_url: s.homepage_url,
    category: s.category,
    town: s.town ?? '',
    independence_group: s.independence_group,
    retrieval_method: s.retrieval_method,
    start_url: s.start_url ?? '',
    feed_url: s.feed_url ?? '',
    crawl_source: s.crawl_source,
    crawl_render: s.crawl_render,
    crawl_limit: s.crawl_limit,
    crawl_depth: s.crawl_depth,
    crawl_max_age: s.crawl_max_age,
    cadence: s.cadence,
    include_patterns: (s.include_patterns ?? []).join('\n'),
    exclude_patterns: (s.exclude_patterns ?? []).join('\n'),
    enabled: s.enabled,
    notes: s.notes ?? '',
  };
}

function splitLines(v: string): string[] | null {
  const out = v
    .split('\n')
    .map((l) => l.trim())
    .filter((l) => l.length > 0);
  return out.length > 0 ? out : null;
}

export default function SourceEditorPage() {
  const { id } = useParams<{ id?: string }>();
  const isNew = id === undefined;
  const navigate = useNavigate();

  const [form, setForm] = useState<FormState>(EMPTY);
  const [permissions, setPermissions] = useState<SourcePermissionCheck[]>([]);
  const [expectedUpdatedAt, setExpectedUpdatedAt] = useState<number | null>(null);
  const [loaded, setLoaded] = useState(isNew);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (isNew) return;
    void (async () => {
      try {
        const { source, permissions } = await sourcesApi.get(id!);
        setForm(toForm(source));
        setPermissions(permissions);
        setExpectedUpdatedAt(source.updated_at);
        setLoaded(true);
      } catch (e) {
        setErr(e instanceof Error ? e.message : 'Failed to load');
      }
    })();
  }, [id, isNew]);

  function set<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm((f) => ({ ...f, [key]: value }));
  }

  async function save() {
    setBusy(true);
    setErr(null);
    try {
      const payload = {
        slug: form.slug,
        name: form.name,
        homepage_url: form.homepage_url,
        category: form.category,
        town: form.town.trim() || null,
        independence_group: form.independence_group,
        retrieval_method: form.retrieval_method,
        start_url: form.start_url.trim() || null,
        feed_url: form.feed_url.trim() || null,
        crawl_source: form.crawl_source,
        crawl_render: form.crawl_render,
        crawl_limit: form.crawl_limit,
        crawl_depth: form.crawl_depth,
        crawl_max_age: form.crawl_max_age,
        cadence: form.cadence,
        include_patterns: splitLines(form.include_patterns),
        exclude_patterns: splitLines(form.exclude_patterns),
        enabled: form.enabled,
        notes: form.notes.trim() || null,
      };

      if (isNew) {
        const { id: newId } = await sourcesApi.create(payload);
        navigate(`/settings/sources/${newId}`, { replace: true });
      } else {
        const { updated_at } = await sourcesApi.update(id!, {
          ...payload,
          expected_updated_at: expectedUpdatedAt!,
        });
        setExpectedUpdatedAt(updated_at);
      }
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : e instanceof Error ? e.message : 'Failed to save');
    } finally {
      setBusy(false);
    }
  }

  async function del() {
    if (
      !confirm(
        `Delete "${form.name}"? Its captured snapshots are deleted too — disabling is usually what you want instead.`,
      )
    )
      return;
    setBusy(true);
    try {
      await sourcesApi.delete(id!);
      navigate('/settings/sources');
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'Failed to delete');
      setBusy(false);
    }
  }

  if (!loaded) {
    return <p className="text-sm text-slate-400">{err ?? 'Loading…'}</p>;
  }

  return (
    <div>
      <header className="flex items-center justify-between gap-3 mb-5">
        <button className="btn-ghost" onClick={() => navigate('/settings/sources')}>
          <ArrowLeft size={14} /> Sources
        </button>
        <div className="flex items-center gap-2">
          {!isNew && (
            <button className="btn-ghost text-red-600" onClick={del} disabled={busy}>
              <Trash2 size={14} /> Delete
            </button>
          )}
          <button className="btn-primary" onClick={save} disabled={busy}>
            <Save size={14} /> {busy ? 'Saving…' : 'Save'}
          </button>
        </div>
      </header>

      {err && <p className="text-sm text-red-600 mb-4">{err}</p>}

      <div className="space-y-6">
        <Section title="Identity">
          <Field label="Name">
            <input className="input" value={form.name} onChange={(e) => set('name', e.target.value)} />
          </Field>
          <Field label="Slug" hint="Lowercase words separated by hyphens. Stable — used in traces and R2 keys.">
            <input className="input" value={form.slug} onChange={(e) => set('slug', e.target.value)} />
          </Field>
          <Field label="Homepage URL">
            <input
              className="input"
              value={form.homepage_url}
              onChange={(e) => set('homepage_url', e.target.value)}
            />
          </Field>
          <Field label="Category">
            <select
              className="input"
              value={form.category}
              onChange={(e) => set('category', e.target.value as SourceCategory)}
            >
              {CATEGORIES.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Town" hint="Coverage town for municipal sources. Blank for regional and service data.">
            <input className="input" value={form.town} onChange={(e) => set('town', e.target.value)} />
          </Field>
          <Field
            label="Independence group"
            hint="Corroboration counts groups, not domains. Co-owned or syndicated properties share a group so the gate can't be fooled by wire copy."
          >
            <input
              className="input"
              value={form.independence_group}
              onChange={(e) => set('independence_group', e.target.value)}
            />
          </Field>
        </Section>

        <Section title="Retrieval">
          <Field label="Method">
            <select
              className="input"
              value={form.retrieval_method}
              onChange={(e) => set('retrieval_method', e.target.value as RetrievalMethod)}
            >
              {METHODS.map((m) => (
                <option key={m} value={m}>
                  {m}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Start URL" hint="Crawl entry point. Defaults to the homepage.">
            <input
              className="input"
              value={form.start_url}
              onChange={(e) => set('start_url', e.target.value)}
            />
          </Field>
          <Field label="Feed URL" hint="Audited separately — a site can disallow / while allowing /feed.">
            <input
              className="input"
              value={form.feed_url}
              onChange={(e) => set('feed_url', e.target.value)}
            />
          </Field>
          <Field label="Discovery mode">
            <select
              className="input"
              value={form.crawl_source}
              onChange={(e) => set('crawl_source', e.target.value as CrawlSourceMode)}
            >
              {CRAWL_SOURCES.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Cadence">
            <select
              className="input"
              value={form.cadence}
              onChange={(e) => set('cadence', e.target.value as SourceCadence)}
            >
              {CADENCES.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </Field>
          <Field
            label="Render with a browser"
            hint="Off is a cheap Workers-side fetch and is not billed for browser time. Only turn on for sources that genuinely build content client-side."
          >
            <label className="flex items-center gap-2 text-sm text-slate-700">
              <input
                type="checkbox"
                checked={form.crawl_render}
                onChange={(e) => set('crawl_render', e.target.checked)}
              />
              Execute JavaScript
            </label>
          </Field>
          <Field label="Page limit">
            <input
              type="number"
              className="input"
              value={form.crawl_limit}
              onChange={(e) => set('crawl_limit', Number(e.target.value))}
            />
          </Field>
          <Field label="Link depth">
            <input
              type="number"
              className="input"
              value={form.crawl_depth}
              onChange={(e) => set('crawl_depth', Number(e.target.value))}
            />
          </Field>
          <Field label="Cache max age (seconds)" hint="How long a cached copy may be reused. Max 604800.">
            <input
              type="number"
              className="input"
              value={form.crawl_max_age}
              onChange={(e) => set('crawl_max_age', Number(e.target.value))}
            />
          </Field>
          <Field label="Include patterns" hint="One per line. Blank means no restriction.">
            <textarea
              className="input font-mono text-xs"
              rows={3}
              value={form.include_patterns}
              onChange={(e) => set('include_patterns', e.target.value)}
            />
          </Field>
          <Field label="Exclude patterns" hint="One per line. Excludes take priority over includes.">
            <textarea
              className="input font-mono text-xs"
              rows={3}
              value={form.exclude_patterns}
              onChange={(e) => set('exclude_patterns', e.target.value)}
            />
          </Field>
        </Section>

        <Section title="Status">
          <Field
            label="Enabled"
            hint="Turning a source off here marks it as a human decision, which stops the weekly auditor from re-enabling it."
          >
            <label className="flex items-center gap-2 text-sm text-slate-700">
              <input
                type="checkbox"
                checked={form.enabled}
                onChange={(e) => set('enabled', e.target.checked)}
              />
              Include in retrieval
            </label>
          </Field>
          <Field label="Notes">
            <textarea
              className="input"
              rows={4}
              value={form.notes}
              onChange={(e) => set('notes', e.target.value)}
            />
          </Field>
        </Section>

        {!isNew && <PermissionHistory checks={permissions} />}
      </div>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="bg-white border border-slate-200 rounded-md p-4">
      <h3 className="text-sm font-medium text-slate-900 mb-3">{title}</h3>
      <div className="grid sm:grid-cols-2 gap-4">{children}</div>
    </section>
  );
}

function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <label className="block text-xs font-medium text-slate-700 mb-1">{label}</label>
      {children}
      {hint && <p className="text-xs text-slate-400 mt-1">{hint}</p>}
    </div>
  );
}

function verdictTone(v: string): string {
  if (v === 'allowed') return 'bg-green-100 text-green-800';
  if (v === 'disallowed' || v === 'signal_blocked') return 'bg-red-100 text-red-800';
  return 'bg-slate-200 text-slate-600';
}

function PermissionHistory({ checks }: { checks: SourcePermissionCheck[] }) {
  return (
    <section className="bg-white border border-slate-200 rounded-md p-4">
      <h3 className="text-sm font-medium text-slate-900 mb-1">Permission history</h3>
      <p className="text-xs text-slate-500 mb-3">
        Written by the weekly audit, from Cloudflare&rsquo;s network. Verdicts are per mechanism:
        a site can allow the crawler and block Anthropic&rsquo;s fetch, which decides where its
        text has to come from.
      </p>
      {checks.length === 0 ? (
        <p className="text-sm text-slate-400">
          Never audited. Retrieval skips this source until it has been.
        </p>
      ) : (
        <ul className="divide-y divide-slate-100">
          {checks.map((c) => (
            <li key={c.id} className="py-2.5">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-xs text-slate-500 tabular-nums">
                  {new Date(c.checked_at).toLocaleString('en-US', {
                    timeZone: 'America/New_York',
                    dateStyle: 'medium',
                    timeStyle: 'short',
                  })}
                </span>
                <span
                  className={`text-[10px] uppercase tracking-wide font-semibold px-1.5 py-0.5 rounded ${verdictTone(c.crawl_verdict)}`}
                >
                  crawl: {c.crawl_verdict}
                </span>
                <span
                  className={`text-[10px] uppercase tracking-wide font-semibold px-1.5 py-0.5 rounded ${verdictTone(c.claude_verdict)}`}
                >
                  claude: {c.claude_verdict}
                </span>
                {c.changed && (
                  <span className="text-[10px] uppercase tracking-wide font-semibold bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded">
                    changed
                  </span>
                )}
                {c.robots_status !== null && (
                  <span className="text-xs text-slate-400">HTTP {c.robots_status}</span>
                )}
                {c.crawl_delay !== null && (
                  <span className="text-xs text-slate-400">crawl-delay {c.crawl_delay}s</span>
                )}
              </div>
              {c.content_signal && (
                <p className="text-xs text-slate-500 mt-1 font-mono">
                  Content-Signal: {c.content_signal}
                </p>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
